import React from 'react';

const timeline = [
  { year: '2015-2024', title: 'PAT scheme groundwork', desc: 'Perform, Achieve and Trade established the industrial efficiency base and validated a market-led compliance approach.', color: 'var(--neon-green)' },
  { year: 'Dec 2022', title: 'Energy Conservation amendment', desc: 'The legal basis for the carbon market was expanded, enabling certificate issuance and market administration.', color: 'var(--neon-cyan)' },
  { year: 'June 2023', title: 'CCTS notified', desc: 'India formally notified the Carbon Credit Trading Scheme and defined the compliance-market structure.', color: 'var(--neon-blue)' },
  { year: 'July 2024', title: 'Compliance regulations adopted', desc: 'Detailed compliance procedures clarified the intensity-based baseline-and-credit model.', color: 'var(--neon-orange)' },
  { year: 'March 2025', title: 'Offset methodologies approved', desc: 'Voluntary offset methodologies expanded the project-development side of the market.', color: 'var(--neon-pink)' },
  { year: 'Oct 2025', title: 'Initial plant targets notified', desc: 'First batches of obligated plants received notified GHG intensity targets.', color: 'var(--neon-yellow)' },
  { year: 'Jan 2026', title: 'Additional sectors added', desc: 'Refinery, petrochemicals, and textiles received final targets under the notified framework.', color: 'var(--neon-purple)' },
  { year: '2026 onwards', title: 'Operational go-live pending', desc: 'Public traded-price feeds and operational exchange trading still depend on official notices.', color: 'var(--text)' },
];

const sectors = [
  { name: 'Cement', status: 'Notified', detail: 'Reduction range 0.85% to 7.6%', color: 'var(--neon-cyan)' },
  { name: 'Aluminium', status: 'Notified', detail: 'Reduction range 1.9% to 7.06%', color: 'var(--neon-cyan)' },
  { name: 'Pulp and Paper', status: 'Notified', detail: 'Reduction up to 15% across the first cycle', color: 'var(--neon-cyan)' },
  { name: 'Chlor-Alkali', status: 'Notified', detail: 'Reduction range 1.32% to 4.53%', color: 'var(--neon-cyan)' },
  { name: 'Iron and Steel', status: 'Pending', detail: 'Additional target pathway expected', color: 'var(--neon-yellow)' },
  { name: 'Petroleum Refinery', status: 'Notified', detail: 'Final targets notified in January 2026', color: 'var(--neon-cyan)' },
  { name: 'Petrochemicals', status: 'Notified', detail: 'Final targets notified in January 2026', color: 'var(--neon-cyan)' },
  { name: 'Fertilisers', status: 'Pending', detail: 'Target expansion remains expected', color: 'var(--neon-yellow)' },
  { name: 'Textiles', status: 'Notified', detail: 'Final targets notified in January 2026', color: 'var(--neon-cyan)' },
];

const institutions = [
  { name: 'Ministry of Power', desc: 'Leads overall CCTS implementation and oversight', color: 'var(--neon-green)' },
  { name: 'MoEFCC', desc: 'Notifies GHG intensity targets and obligated entities', color: 'var(--neon-cyan)' },
  { name: 'BEE', desc: 'Administers the market and accreditation process', color: 'var(--neon-orange)' },
  { name: 'CERC', desc: 'Regulates exchange trading and market supervision', color: 'var(--neon-pink)' },
  { name: 'GCI Registry', desc: 'Operates the national certificate registry and ledger', color: 'var(--neon-purple)' },
  { name: 'IEX / PXIL', desc: 'Expected exchange channels for carbon certificate trading', color: 'var(--neon-yellow)' },
];

const companies = [
  { name: 'NTPC Ltd.', tag: 'PSU / obligated entity', color: 'var(--neon-green)' },
  { name: 'UltraTech Cement', tag: 'Cement sector participant', color: 'var(--neon-orange)' },
  { name: 'Tata Steel', tag: 'Industrial compliance exposure', color: 'var(--neon-cyan)' },
  { name: 'ReNew Power', tag: 'Renewable project developer', color: 'var(--neon-green)' },
  { name: 'IEX', tag: 'Trading platform pathway', color: 'var(--neon-purple)' },
  { name: 'PXIL', tag: 'Trading platform pathway', color: 'var(--neon-purple)' },
  { name: 'EKI Energy', tag: 'Carbon project and advisory player', color: 'var(--neon-pink)' },
  { name: 'Adani Green', tag: 'Renewable generation presence', color: 'var(--neon-green)' },
];

export default function IndiaMarket() {
  return (
    <div className="page-shell page-stack">
      <section className="surface-panel glow-surface" style={{ padding: '1.8rem' }}>
        <div className="section-stack">
          <div className="section-tag" style={{ color: 'var(--neon-orange)' }}>India carbon market</div>
          <h1 className="section-title">India market context, redesigned around clarity instead of noise</h1>
          <p className="section-copy">
            This page now reads more like a modern product story: what has happened, what is operational, who the key institutions are, and what a user should understand before expecting live exchange behavior.
          </p>
        </div>

        <div className="metric-strip" style={{ marginTop: '1.5rem' }}>
          <div className="metric-tile">
            <div className="eyebrow">Covered sectors</div>
            <div className="value" style={{ color: 'var(--neon-orange)' }}>9</div>
          </div>
          <div className="metric-tile">
            <div className="eyebrow">Initial obligated plants</div>
            <div className="value" style={{ color: 'var(--neon-green)' }}>282+</div>
          </div>
          <div className="metric-tile">
            <div className="eyebrow">Model type</div>
            <div className="value" style={{ color: 'var(--neon-cyan)' }}>Intensity based</div>
          </div>
          <div className="metric-tile">
            <div className="eyebrow">Trading status</div>
            <div className="value" style={{ color: 'var(--neon-pink)' }}>Operational notices pending</div>
          </div>
        </div>
      </section>

      <section className="section-grid-2">
        <div className="surface-panel" style={{ padding: '1.5rem' }}>
          <div className="section-stack">
            <div className="section-tag">What is happening now</div>
            <div className="headline-md">India has the framework in place. Operations are still being phased into market reality.</div>
            <p className="copy-sm">
              BEE registration, compliance notifications, and sector-level target setting have moved the market into a serious pre-operational stage. The important UX point is honesty: a user should understand that rulemaking and registry readiness are ahead of public live trading feeds.
            </p>
          </div>

          <div className="story-grid" style={{ marginTop: '1.4rem' }}>
            <div className="story-card">
              <div className="eyebrow">Registry</div>
              <div className="headline-sm" style={{ marginTop: '0.35rem' }}>GCI-led certificate record</div>
              <div className="copy-sm" style={{ marginTop: '0.45rem' }}>The registry remains central to certificate issuance, transfer, and surrender control.</div>
            </div>
            <div className="story-card">
              <div className="eyebrow">Exchanges</div>
              <div className="headline-sm" style={{ marginTop: '0.35rem' }}>IEX and PXIL pathway</div>
              <div className="copy-sm" style={{ marginTop: '0.45rem' }}>Trading is expected through the power exchange infrastructure once operational notices land.</div>
            </div>
            <div className="story-card">
              <div className="eyebrow">User takeaway</div>
              <div className="headline-sm" style={{ marginTop: '0.35rem' }}>Reference first, execution later</div>
              <div className="copy-sm" style={{ marginTop: '0.45rem' }}>That is why the product surfaces reference snapshots instead of pretending to be a live national exchange.</div>
            </div>
          </div>
        </div>

        <div className="surface-panel" style={{ padding: '1.5rem' }}>
          <div className="section-stack">
            <div className="section-tag" style={{ color: 'var(--neon-pink)' }}>Timeline</div>
            <div className="headline-md">What has already been done</div>
          </div>
          <div className="timeline-list" style={{ marginTop: '1.2rem' }}>
            {timeline.map((item) => (
              <div key={item.year} className="timeline-card">
                <div className="eyebrow" style={{ color: item.color }}>{item.year}</div>
                <div>
                  <div className="headline-sm">{item.title}</div>
                  <div className="copy-sm" style={{ marginTop: '0.4rem' }}>{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="surface-panel" style={{ padding: '1.5rem' }}>
        <div className="section-header-row">
          <div className="section-stack">
            <div className="section-tag" style={{ color: 'var(--neon-purple)' }}>Covered industries</div>
            <div className="headline-md">Nine sectors under the current coverage story</div>
          </div>
        </div>

        <div className="market-grid-3" style={{ marginTop: '1rem' }}>
          {sectors.map((sector) => (
            <div key={sector.name} className="market-card">
              <div className="headline-sm">{sector.name}</div>
              <div className="copy-sm" style={{ marginTop: '0.35rem' }}>{sector.detail}</div>
              <div className="pills-row" style={{ marginTop: '0.8rem' }}>
                <span className={`badge ${sector.status === 'Notified' ? 'badge-cyan' : 'badge-yellow'}`}>{sector.status}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="section-grid-2">
        <div className="surface-panel" style={{ padding: '1.5rem' }}>
          <div className="section-stack">
            <div className="section-tag">Institutions</div>
            <div className="headline-md">The operating stack behind the market</div>
          </div>
          <div className="market-grid-3" style={{ marginTop: '1rem', gridTemplateColumns: 'repeat(2, 1fr)' }}>
            {institutions.map((item) => (
              <div key={item.name} className="info-card">
                <div className="headline-sm" style={{ color: item.color }}>{item.name}</div>
                <div className="copy-sm" style={{ marginTop: '0.4rem' }}>{item.desc}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="surface-panel" style={{ padding: '1.5rem' }}>
          <div className="section-stack">
            <div className="section-tag" style={{ color: 'var(--neon-yellow)' }}>Ecosystem players</div>
            <div className="headline-md">Companies already associated with the space</div>
          </div>
          <div className="market-grid-4" style={{ marginTop: '1rem', gridTemplateColumns: 'repeat(2, 1fr)' }}>
            {companies.map((company) => (
              <div key={company.name} className="info-card">
                <div className="headline-sm">{company.name}</div>
                <div className="pills-row" style={{ marginTop: '0.7rem' }}>
                  <span className="badge" style={{ color: company.color, borderColor: 'rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)' }}>{company.tag}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="surface-panel glow-surface" style={{ padding: '1.6rem' }}>
        <div className="section-stack">
          <div className="section-tag">Future outlook</div>
          <div className="headline-md">What the next operating phases are likely to look like</div>
        </div>
        <div className="story-grid" style={{ marginTop: '1rem' }}>
          <div className="story-card">
            <div className="eyebrow" style={{ color: 'var(--neon-green)' }}>2026-2027</div>
            <div className="copy-sm" style={{ marginTop: '0.45rem' }}>Registry, verification, and exchange operations continue to move from rulebook to execution.</div>
          </div>
          <div className="story-card">
            <div className="eyebrow" style={{ color: 'var(--neon-cyan)' }}>2027-2030</div>
            <div className="copy-sm" style={{ marginTop: '0.45rem' }}>Coverage, target strength, and MRV controls are likely to deepen alongside India’s NDC pathway.</div>
          </div>
          <div className="story-card">
            <div className="eyebrow" style={{ color: 'var(--neon-orange)' }}>2030 and beyond</div>
            <div className="copy-sm" style={{ marginTop: '0.45rem' }}>A larger carbon-market footprint is plausible, but sector expansion and any international linking remain future policy questions.</div>
          </div>
        </div>
      </section>
    </div>
  );
}
